import { BaseViewModel } from "@iot-platform/core";
import { Sensor } from "@station/domain/models/sensor.model";
import type { Station } from "@station/domain/models/station.model";
import { SensorGroupGetAllUseCase } from "@station/domain/usecases/sensor-group-get-all.usecase";
import { inject, injectable } from "inversify";
import { DateTime } from "luxon";
import { action, makeObservable, observable, runInAction } from "mobx";

interface ModalGraphProps {
    visible: boolean;
    stationId?: number;
    sensorIds: number[];
    sensors: Readonly<Sensor>[];
    from?: number;
    to?: number;
}

@injectable()
export class CustomGraphFilterViewmodel extends BaseViewModel {
    @observable sensors: Readonly<Sensor>[] = [];
    @observable selectedSensors: number[] = [];
    @observable currentStation: Readonly<Station> | null = null;

    @observable from: DateTime = DateTime.now().minus({ days: 1 });
    @observable to: DateTime = DateTime.now();
    @observable errors: { from?: string; to?: string } = {};

    @observable initialLoading = true;

    @observable modalGraphProps: ModalGraphProps = {
        visible: false,
        sensorIds: [],
        sensors: [],
    };

    constructor(
        @inject(SensorGroupGetAllUseCase)
        private readonly sensorGroupGetAllUseCase: SensorGroupGetAllUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override async didMount(): Promise<void> {
        await this.initViewData();
    }

    @action
    async initViewData(): Promise<void> {
        if (!this.currentStation) {
            runInAction(() => {
                this.initialLoading = false;
            });
            return;
        }

        await this.fetchSensors();
        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    setStation = (station: Readonly<Station>): void => {
        runInAction(() => {
            this.currentStation = station;
        });
    };

    @action
    private async fetchSensors(): Promise<void> {
        if (!this.currentStation) throw new Error("Station not set");

        const sensorGroups = await this.sensorGroupGetAllUseCase.execute(
            this.currentStation.id.value,
        );

        // Dedupe sensors that share the same `code` across vargroups (e.g.
        // TA_0/HR_0 declared in two vargroups); they render with the same
        // translated label and would otherwise duplicate in the selector.
        // Sensors with `code === null` are kept as-is — their fallback
        // label is `sensor.name`, which is per-row distinct.
        const seenCodes = new Set<string>();
        const sensors: Readonly<Sensor>[] = [];
        for (const sensorGroup of sensorGroups.sensorGroups) {
            for (const sensor of sensorGroup.sensors) {
                if (sensor.code !== null) {
                    if (seenCodes.has(sensor.code)) continue;
                    seenCodes.add(sensor.code);
                }
                sensors.push(sensor);
            }
        }

        runInAction(() => {
            this.sensors = sensors;
        });
    }

    @action
    setDates(
        from: DateTime,
        to: DateTime,
        errors: { from?: string; to?: string },
    ): void {
        runInAction(() => {
            this.from = from;
            this.to = to;
            this.errors = errors;
        });
    }

    @action
    closeModal(): void {
        this.modalGraphProps = { visible: false, sensorIds: [], sensors: [] };
    }

    @action
    generateGraph(): void {
        if (!this.selectedSensors.length) return;
        if (!this.currentStation) return;
        if (this.errors.from || this.errors.to) return;

        const fromUnix = Math.floor(this.from.toSeconds());
        const toUnix = Math.floor(this.to.toSeconds());
        if (
            Number.isNaN(fromUnix) ||
            Number.isNaN(toUnix) ||
            fromUnix >= toUnix
        )
            return;

        this.modalGraphProps = {
            visible: true,
            stationId: Number(this.currentStation.id.value),
            sensorIds: [...this.selectedSensors],
            sensors: [...this.sensors],
            from: fromUnix,
            to: toUnix,
        };
    }
}
